import { createClient } from '@supabase/supabase-js';
// If Database type doesn't exist, use any as a fallback
// import type { Database } from '../types/supabase';

// Environment variables should be defined in your .env file
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate env vars with improved logging - only on first load
if (!supabaseUrl || supabaseUrl.includes('your-project-id') || supabaseUrl.includes('your-real-project-id')) {
  console.error('⚠️ CRITICAL ERROR: Invalid or missing VITE_SUPABASE_URL. Please update your .env file with your actual Supabase URL.');
  // Throw error in development to make it obvious
  if (import.meta.env.DEV) {
    console.warn('In development mode - please fix your environment variables before continuing');
    // Don't throw - allow app to load with warnings
  }
}

if (!supabaseAnonKey || supabaseAnonKey.includes('your-supabase-anon-key') || supabaseAnonKey.includes('your-actual-supabase-anon-key')) {
  console.error('⚠️ CRITICAL ERROR: Invalid or missing VITE_SUPABASE_ANON_KEY. Please update your .env file with your actual Supabase anon key.');
  // Throw error in development to make it obvious
  if (import.meta.env.DEV) {
    console.warn('In development mode - please fix your environment variables before continuing');
    // Don't throw - allow app to load with warnings
  }
}

// Log initialization only once in development mode
if (import.meta.env.DEV) {
  console.log(`Initializing Supabase client with URL: ${supabaseUrl}`);
}

// Create a stable singleton instance of the Supabase client
// This ensures only one client is created throughout the application
let supabaseInstance: any = null;

export const getSupabaseClient = () => {
  if (supabaseInstance) return supabaseInstance;
  
  // Create new instance if none exists yet
  supabaseInstance = createClient(
    supabaseUrl || '', 
    supabaseAnonKey || '',
    {
      auth: {
        persistSession: true, // Enable session persistence
        autoRefreshToken: true, // Automatically refresh token
        detectSessionInUrl: true, // Detect session from URL
      },
    }
  );
  
  // Only log in development
  if (import.meta.env.DEV) {
    console.log('Supabase client instance created');
  }
  
  return supabaseInstance;
};

// Export a stable singleton instance
export const supabase = getSupabaseClient();

// Test connection only in development mode
if (import.meta.env.DEV) {
  supabase.auth.getSession().then((result: any) => {
    if (result.error) {
      console.error('❌ Supabase connection test failed:', result.error.message);
    } else {
      console.log('✅ Supabase connection test passed');
    }
  });
}

// Export commonly used tables as convenience functions
// Table names match the actual Supabase schema
export const getAppointmentsTable = () => supabase.from('appointment_bookings');
export const getDoctorsTable = () => supabase.from('clinic_specialists');
export const getLocationsTable = () => supabase.from('caf_bases');
export const getAvailableTimesTable = () => supabase.from('clinic_slots');

// Type definitions aligned with the real Supabase schema

/** Matches the appointment_bookings table */
export interface AppointmentData {
  id: string;
  session_id?: string | null;
  user_id: string; // TEXT — the authenticated user's UUID stored as text
  appointment_type?: string | null;
  preferred_date?: string | null; // date YYYY-MM-DD
  preferred_time?: string | null; // time HH:MM:SS
  clinic_location?: string | null;
  status: string;
  notes?: string | null;
  service_code?: string | null;
  provider_id?: string | null;
  priority?: string | null;
  reason?: string | null;
  scheduled_at?: string | null;
  scheduled_by?: string | null;
  slot_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

/** Matches the clinic_specialists table */
export interface DoctorData {
  id: string;
  base_id?: string | null;
  display_name?: string | null;
  role?: string | null;
  service_code?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  active?: boolean | null;
  created_at?: string | null;
}

/** Matches the caf_bases table (locations) — columns may vary; extend as needed */
export interface LocationData {
  id: string;
  [key: string]: unknown;
}

/** Matches the clinic_slots table */
export interface AvailableTimeData {
  id: string;
  specialty?: string | null;
  specialist_id?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  capacity?: number | null;
  booked_count?: number | null;
  status?: string | null;
  location?: string | null;
  created_at?: string | null;
} 